import React from 'react';
import { Flex, Card, CardContent, Heading, Text, Button, ThemeProvider, Divider, TextField, Spinner } from '@chatsdet/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  useVerifyEmailByToken,
  useVerifyEmailByCode,
  useResendVerification,
  authErrorMessage,
  isVerificationEmailSendFailure,
} from '../../hooks/useEmailAuth';
import type { VerifyEmailSearchParams } from '../../features/auth/routes';
import { useInviteToken, clearInviteToken } from '../../hooks/useInviteToken';
import { BRAND } from '../../brand';
import { CofactorMark } from '../components/CofactorMark';

export function VerifyEmailPage() {
  const _navigate = useNavigate();
  const search = useSearch({ strict: false }) as VerifyEmailSearchParams;
  const { email, token, sendFailed } = search;
  const inviteToken = useInviteToken(search.invite);
  const name = BRAND.name;

  // Invite is auto-accepted during verification; a sign-up that began on a deep link (validated
  // by the route through sanitizeReturnPath) returns there, otherwise home.
  const redirectUrl = search.returnTo ?? '/';

  // Verification is done once the server has set the session. Leave the page
  // right away with a full load: the form must not stay on screen for a second
  // submit of an already-consumed code.
  const finishVerification = () => {
    setSuccess(true);
    clearInviteToken();
    window.location.replace(redirectUrl);
  };

  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [resendSuccess, setResendSuccess] = React.useState(false);
  // Outcome of the latest resend, once the user has tried one. Until then the
  // signup outcome (`sendFailed`) decides, so the page never claims an email went
  // out when it did not. Derived, not copied into state: this page can render
  // before the router's search has caught up with the new location.
  const [resendEmailSent, setResendEmailSent] = React.useState<boolean | null>(null);
  const emailSent = resendEmailSent ?? sendFailed !== true;

  const verifyByTokenMutation = useVerifyEmailByToken();
  const verifyByCodeMutation = useVerifyEmailByCode();
  const resendMutation = useResendVerification();

  // Auto-verify if token is in URL (magic link)
  React.useEffect(() => {
    if (token && !success) {
      verifyByTokenMutation.mutate(
        { token, invite: inviteToken },
        {
          onSuccess: finishVerification,
          onError: (err) => {
            setError(authErrorMessage(err, 'Verification failed. The link may have expired.'));
          },
        }
      );
    }
  }, [token, success, inviteToken, redirectUrl]);

  const handleCodeVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Email is required for code verification');
      return;
    }

    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      await verifyByCodeMutation.mutateAsync({ email, code, invite: inviteToken });
      finishVerification();
    } catch (err) {
      setError(authErrorMessage(err, 'Verification failed. Please try again.'));
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Email is required to resend verification');
      return;
    }

    setError(null);
    setResendSuccess(false);

    try {
      await resendMutation.mutateAsync({ email, invite: inviteToken, returnTo: search.returnTo });
      setResendSuccess(true);
      setResendEmailSent(true);
    } catch (err) {
      if (isVerificationEmailSendFailure(err)) setResendEmailSent(false);
      setError(authErrorMessage(err, 'Failed to resend verification email. Please try again.'));
    }
  };

  const isVerifying = verifyByTokenMutation.isPending || verifyByCodeMutation.isPending;

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

        <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
          <CardContent className="p-8 space-y-8">
            {/* Logo/Brand Section */}
            <div className="text-center space-y-3">
              <div className="flex justify-center mb-2">
                <CofactorMark size={80} className="text-foreground" />
              </div>
              <Heading level={1} className="text-2xl font-bold text-foreground">
                {success ? 'Email Verified!' : 'Verify your email'}
              </Heading>
              {!success && email && (
                <Text className="text-muted-foreground">
                  {emailSent
                    ? 'We sent a verification email to '
                    : "We couldn't send a verification email to "}
                  <span className="font-medium text-foreground">{email}</span>
                  {!emailSent && '. Use the button below to try again.'}
                </Text>
              )}
            </div>

            <Divider />

            {success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <Text className="text-muted-foreground">
                  Your email has been verified. Taking you to the app...
                </Text>
                <Spinner size="sm" />
              </div>
            ) : token && isVerifying ? (
              <div className="text-center space-y-4">
                <Spinner size="lg" />
                <Text className="text-muted-foreground">Verifying your email...</Text>
              </div>
            ) : (
              <div className="space-y-6">
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <Text size="sm" className="text-destructive">
                      {error}
                    </Text>
                  </div>
                )}

                {resendSuccess && (
                  <div className="p-3 bg-success/10 border border-success/20 rounded-md">
                    <Text size="sm" className="text-success">
                      Verification email sent! Check your inbox.
                    </Text>
                  </div>
                )}

                {emailSent && (
                  <div className="text-center space-y-2">
                    <Text size="sm" className="text-muted-foreground">
                      Click the link in the email, or enter the 6-digit code below:
                    </Text>
                  </div>
                )}

                <form onSubmit={handleCodeVerify} className="space-y-4">
                  <TextField
                    label="Verification Code"
                    type="text"
                    value={code}
                    onChange={(val) => setCode(val.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    className="text-center text-2xl"
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    isDisabled={isVerifying}
                  >
                    {verifyByCodeMutation.isPending ? (
                      <Flex align="center" gap="sm">
                        <Spinner size="sm" />
                        <span>Verifying...</span>
                      </Flex>
                    ) : (
                      'Verify'
                    )}
                  </Button>
                </form>

                <Divider />

                <div className="text-center space-y-3">
                  <Text size="sm" className="text-muted-foreground">
                    Didn't receive the email?
                  </Text>
                  <Button
                    variant="secondary"
                    size="md"
                    onPress={handleResend}
                    isDisabled={resendMutation.isPending}
                  >
                    {resendMutation.isPending ? (
                      <Flex align="center" gap="sm">
                        <Spinner size="sm" />
                        <span>Sending...</span>
                      </Flex>
                    ) : (
                      'Resend verification email'
                    )}
                  </Button>
                </div>

                <div className="text-center">
                  {search.returnTo ? (
                    <a href={search.returnTo} className="text-sm text-primary hover:underline">
                      Back to sign in
                    </a>
                  ) : (
                    <Link
                      to="/login"
                      search={{ invite: inviteToken }}
                      className="text-sm text-primary hover:underline"
                    >
                      Back to sign in
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <Text size="xs" className="text-muted-foreground">
            © {new Date().getFullYear()} {name}. All rights reserved.
          </Text>
        </div>
      </div>
    </ThemeProvider>
  );
}
